// src/routes/_main/index.tsx
import OrdersOverview from "@/components/charts/orders-overview";
import InfoCard from "@/components/info-card";
import Onboarding from "@/components/onboarding";
import { buildMetadata } from "@/lib/metadata";
import { OrderService } from "@/services/order-services";
import { UserProfileService } from "@/services/user-profile-service";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ShoppingCart } from "lucide-react";

export const Route = createFileRoute("/_main/")({
  component: Index,
  head: () => ({
    meta: buildMetadata({
      title: "Dashboard",
      description: "View your dashboard",
      keywords: ["dashboard", "home", "overview"],
    }),
  }),
  loader: async () => {
    // 1) Try to get profile (returns null on 401/404 or errors)
    const profile = await UserProfileService.getMine();

    // 2) Redirect ONLY if a profile exists AND onboarding is incomplete
    if (profile && !profile.has_completed_onboarding) {
      throw redirect({
        to: "/connect-account",
      });
    }
  },
});

function Index() {
  const now = new Date();
  const startPrevMonth = new Date(
    now.getFullYear(),
    now.getMonth() - 1,
    1,
  ).toISOString();

  const { data: orders } = useQuery({
    queryKey: ["getOrdersSince", startPrevMonth],
    queryFn: () => OrderService.getOrdersSince(startPrevMonth, 100),
  });

  const { data: dashboardData } = useQuery({
    queryKey: ["dashboardData"],
    queryFn: () => OrderService.getDashboardData(),
  });

  return (
    <div className="p-8">
      <Onboarding />

      <div className="my-10">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <InfoCard
            title="Orders"
            score={dashboardData?.total_orders ?? 0}
            context={{
              points: dashboardData?.total_orders_change ?? 0,
              on: "month",
            }}
            Icon={ShoppingCart}
          />
          <InfoCard
            title="Pending"
            score={dashboardData?.pending_orders ?? 0}
            context={{
              points: dashboardData?.pending_orders_change ?? 0,
              on: "month",
            }}
            Icon={ShoppingCart}
          />
          <InfoCard
            title="Processed"
            score={dashboardData?.processed_orders ?? 0}
            context={{
              points: dashboardData?.processed_orders_change ?? 0,
              on: "month",
            }}
            Icon={ShoppingCart}
          />
          <InfoCard
            title="Errors"
            score={dashboardData?.errors ?? 0}
            context={{ points: dashboardData?.errors_change ?? 0, on: "month" }}
            Icon={ShoppingCart}
          />
        </div>
      </div>

      <div className="my-10 flex flex-col gap-4 md:flex-row">
        {dashboardData && orders && (
          <OrdersOverview dashboardData={dashboardData} orders={orders} />
        )}
      </div>
    </div>
  );
}
